import {
  REDUX_ACTIONS,
  GAME_ID,
  ROUND_COUNT,
  LIVES,
  WORD_TYPE,
  WORLDS,
} from '../../data/constants';
import getOnboardingData from '../../data/onboardingStaticData';
import { getCurriculum, getAudioMap } from '../../content/curriculum';

const initStateLevel = {
  currentLevel: 0, // Our current level index
  distractors: [], // Array of distractors in this level
  correctWords: [], // Array of correct words in this level
  targetletters: [], // Array of correct target letters in this level
  targetsounds: [], // Array of correct target sounds in this level
  questionIds: [], // Array of question ids
  questionUuids: [], // Array of question uuids
  masteryNumber: 0, // Number of questions needed answered correctly first try to obtain mastery for the level
  question: 0, // The current question index
  choiceNum: 0, // Total number of choices available this question
  currentQuestionId: 0, // Unique content question identifier
  questionWords: [], // The total list of words to choose from this question
  currentLevelRound: [], // The current round index of each level
  wordType: WORD_TYPE.WORD_VC, // The level type
  lives: LIVES, // The current number of lives the player has
  buttonSelection: null, // The question selected this question
  currentCorrectWord: '', // The current correct word
  currentCorrectTargetLetters: [], // Array of the current target letters
  currentCorrectTargetSounds: [], // Array of the current target sounds
  levelDesign: WORLDS.LEVEL_1, // The design of the current world
  onboarding: false, // Whether or not we're in onboarding
};

export const levelReducer = (state = initStateLevel, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) return state;

  switch (type) {
    // Set the level and grab curriculum data
    case REDUX_ACTIONS.SET_LEVEL: {
      const { level, round } = payload;

      const correctWords = [];
      const correctWordsAssets = [];
      const distractors = [];
      const targetletters = [];
      const targetsounds = [];
      const targetsoundsAssets = [];
      const questionIds = [];
      const questionUuids = [];
      if (state.currentLevelRound.length < level + 1) {
        let i = state.currentLevelRound.length;
        for (i; i < level + 1; i++) {
          state.currentLevelRound.push(0);
        }
      }

      const currentLevelRound = [...state.currentLevelRound];
      currentLevelRound[level] = round === false ? currentLevelRound[level] : round;

      const contentlevel = getCurriculum().levels[level];
      const currentroundindex = currentLevelRound[level];
      const contentround = contentlevel.rounds[currentroundindex];
      const audioLevel = getAudioMap().levels[level];
      const audioRound = audioLevel.rounds[currentroundindex];

      contentround.items.forEach((entry, idx) => {
        correctWords.push(entry.targetWord);
        correctWordsAssets.push(audioRound.items[idx].targetWordAsset);
        distractors.push(entry.distractors);

        // In automaticity we don't have target letters or sounds, add empty arrays
        let tletters = entry.targetLetters;
        if (tletters === undefined) tletters = [];
        targetletters.push(tletters);

        let tsounds = entry.targetSounds;
        if (tsounds === undefined) tsounds = [];

        let tsoundsAssets = audioRound.items[idx].targetSoundsAssets;
        if (tsoundsAssets === undefined) tsoundsAssets = [];

        targetsounds.push(tsounds);
        targetsoundsAssets.push(tsoundsAssets);
        questionIds.push(entry.questionId);
        questionUuids.push(entry.uuid);
      });

      currentLevelRound[level] %= ROUND_COUNT;
      const tLevelDesign = `LEVEL_${(level % 5) + 1}`;
      const content = getCurriculum();
      const { wordType, mastery } = content.levels[level];
      return {
        ...state,
        currentLevel: level,
        correctWords,
        correctWordsAssets,
        distractors,
        targetletters,
        targetsounds,
        targetsoundsAssets,
        questionIds,
        questionUuids,
        masteryNumber: mastery,
        currentLevelRound,
        levelDesign: WORLDS[tLevelDesign],
        wordType,
        lives: LIVES, // Always start at max lives
        onboarding: false,
      };
    }
    // Set the round and grab round data from level
    case REDUX_ACTIONS.SET_QUESTION: {
      if (typeof payload !== 'number') {
        return state;
      }
      // If we're in the main game loop, getting questions
      if (payload < state.correctWords.length && payload >= 0) {
        const questionWords = [];
        questionWords.push(state.correctWords[payload]);
        state.distractors[payload].forEach(entry => questionWords.push(entry));
        // Update the current unique question ID
        const currentQuestionId = Number(state.questionIds[payload]);
        const currentQuestionUuid = state.questionUuids[payload];

        return {
          ...state,
          question: payload,
          questionWords,
          choiceNum: questionWords.length,
          buttonSelection: null,
          currentCorrectWord: state.correctWords[payload],
          currentCorrectWordAsset: state.correctWordsAssets[payload],
          currentCorrectTargetLetters: state.targetletters[payload],
          currentCorrectTargetSounds: state.targetsounds[payload],
          currentCorrectTargetSoundsAssets: state.targetsoundsAssets[payload],
          currentQuestionId,
          currentQuestionUuid,
        };
      } if (payload === state.correctWords.length) {
        // We're past the questions. Round is just used for Curioso positioning
        return {
          ...state,
          question: payload,
        };
      }
      return state;
    }
    case REDUX_ACTIONS.SET_QUESTION_RESULT: {
      let payloadPassed = false;
      // Null value passes since it is a clear
      if (payload === null) payloadPassed = true;
      else {
        // Otherwise check our payload against the possible words
        for (let tI = 0; tI < state.questionWords.length; tI++) {
          if (state.questionWords[tI] === payload) {
            payloadPassed = true;
            break;
          }
        }
      }
      // If the payload didn't pass, return the initial state
      if (!payloadPassed) {
        return state;
      }
      // Otherwise we attach the payload
      return {
        ...state,
        buttonSelection: payload,
      };
    }
    case REDUX_ACTIONS.MODIFY_LIVES:
      if (typeof payload !== 'number') {
        return state;
      }
      return {
        ...state,
        lives: state.lives + payload,
      };

    case REDUX_ACTIONS.SET_ONBOARDING: {
      // if not forcing onboarding and onboarding has already been played, do nothing
      if (!payload.forceOnboarding && payload.onboardingArray.includes(state.wordType)) {
        return state;
      }

      // We haven't onboarded yet!
      const onboardingwords = getOnboardingData(state.wordType).words;

      const {
        correctWords,
        correctWordsAssets,
        distractors,
        targetletters,
        targetsounds,
        targetsoundsAssets,
        questionIds,
        questionUuids,
      } = state;

      return {
        ...state,
        onboarding: true,
        // Modify the word lists in place
        correctWords: [onboardingwords.target, ...correctWords],
        distractors: [onboardingwords.distractors, ...distractors],
        targetletters: [onboardingwords.targetletters, ...targetletters],
        questionIds: ['-1', ...questionIds], // Dummy question id for onboarding
        questionUuids: ['-1', ...questionUuids], // Dummy question uuid for onboarding
        targetsounds: [[], ...targetsounds], // aren't used but we need to insert content
        correctWordsAssets: [{}, ...correctWordsAssets], // aren't used but we need to insert content
        targetsoundsAssets: [[], ...targetsoundsAssets], // aren't used but we need to insert content
      };
    }
    case REDUX_ACTIONS.INCREMENT_CURRENT_ROUND: {
      // Increment the current round of the given level.
      const currentLevelRoundNew = state.currentLevelRound;
      currentLevelRoundNew[payload] += 1;

      // Make sure the new round count is valid.
      currentLevelRoundNew[payload] %= ROUND_COUNT;

      // Return the new round array.
      return {
        ...state,
        currentLevelRound: currentLevelRoundNew,
      };
    }
    default:
      return state;
  }
};

export default levelReducer;
